import { useEffect, useMemo, useState } from 'react'
import TrendChart from './TrendChart'
import { getCreatorAppTrends, type VisitorTrendPoint } from '../../lib/api'
import { TrendingUp } from 'lucide-react'
import IncludeOwnerToggle from './IncludeOwnerToggle'

interface VisitorTrendChartProps {
  appName: string
}

export default function VisitorTrendChart({ appName }: VisitorTrendChartProps) {
  const [data, setData] = useState<VisitorTrendPoint[]>([])
  const [loading, setLoading] = useState(true)
  const [includeOwner, setIncludeOwner] = useState(false)
  const [onlyPublished, setOnlyPublished] = useState(false)

  useEffect(() => {
    getCreatorAppTrends(appName)
      .then(setData)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [appName])

  // Pick base counts off the prod or all-slot field per the published
  // toggle, then optionally fold in owner visits on top. Owner is a
  // single user_id disjoint from the external set, so straight addition
  // for visits is right; for unique-visitors we add 1 on owner-visit
  // days only.
  const chartData = useMemo(() => {
    return data.map((p) => {
      const baseVisits = onlyPublished ? p.visits_prod ?? 0 : p.visits
      // Use the prod-only distinct count when "Only published" is on.
      // Falling back to all-slot unique_visitors here is what produced
      // the "0 visits, 1 unique visitor" inconsistency for days that
      // had an external dev visit but no prod visit.
      const baseUnique = onlyPublished ? p.unique_visitors_prod ?? 0 : p.unique_visitors
      const ownerToday = onlyPublished ? p.owner_visits_prod ?? 0 : p.owner_visits ?? 0
      return {
        ...p,
        visits: baseVisits + (includeOwner ? ownerToday : 0),
        unique_visitors: baseUnique + (includeOwner && ownerToday > 0 ? 1 : 0),
      }
    })
  }, [data, includeOwner, onlyPublished])

  const hasOwnerVisits = data.some((p) => (p.owner_visits ?? 0) > 0)

  if (loading) return <div className="h-64 animate-pulse bg-secondary rounded-xl" />
  if (!data.length) return null

  return (
    <div className="p-6 rounded-xl border border-border bg-card">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <TrendingUp className="w-5 h-5" />
          Visitor Trends <span className="text-xs font-normal text-muted-foreground">(30 days)</span>
        </h2>
        <div className="flex items-center gap-3 flex-wrap">
          <IncludeOwnerToggle
            checked={onlyPublished}
            onCheckedChange={setOnlyPublished}
            label="Only published"
          />
          {(hasOwnerVisits || includeOwner) && (
            <IncludeOwnerToggle checked={includeOwner} onCheckedChange={setIncludeOwner} />
          )}
        </div>
      </div>
      <TrendChart
        data={chartData}
        lines={[
          { dataKey: 'visits', label: 'Total Visits', color: '#3b82f6' },
          { dataKey: 'unique_visitors', label: 'Unique Visitors', color: '#8b5cf6' },
        ]}
        height={250}
      />
    </div>
  )
}
